import { ApolloError } from '@apollo/client';
import { useRoute } from '@react-navigation/native';
import * as Sentry from '@sentry/react-native';
import { dismissAllNotificationsAsync } from 'expo-notifications';
import debounce from 'lodash.debounce';
import React, {
  memo,
  useMemo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { AppState, Platform } from 'react-native';
import { useToast } from 'react-native-toast-notifications';
import uuid from 'react-native-uuid';

import { useWebDrawer } from '@components/Web/Drawer/WebDrawerContext';
import {
  useMessageUpdateSubscription,
  useListMessagesQuery,
  useListNearbyMessagesQuery,
  useListDraftMessagesQuery,
  Message,
  useMarkChatsReadMutation,
  MessageDetailFragmentDoc,
  MessageUpdateLiteFragmentDoc,
  Document,
  DocumentAttributes,
  useListDraftDocumentsQuery,
  ListDraftDocumentsDocument,
  useUpdateMessageIndividualMutation,
  MessageEdge,
  ListMessagesDocument,
  MessageConnection,
  MessageProject,
  MessageTask,
  useGetChatQuery,
  GetChatQuery,
  ListDraftDocumentsQuery,
  MessageDetailFragment,
  LocalFile,
  useMessageDeleteSubscription,
  useListDeletedMessageIdsQuery,
} from '@graphql/generated';
import { insertPublishedMessageEdge } from '@hooks/chatProcessing/insertPublishedMessageEdge';
import { removeDraftMessageEntry } from '@hooks/chatProcessing/removeDraftMessageEntry';
import useActiveChat from '@hooks/useActiveChat';
import { useApolloClient } from '@hooks/useApolloClient';
import useChatInput from '@hooks/useChatInput';
import { useChatUploadManager } from '@hooks/useChatUploadManager';
import { useGetDocumentsByClientIdsQuery } from '@hooks/useGetDocumentsByClientIdsQuery';
import { useGetUserAvatarsByIdsQuery } from '@hooks/useGetUserAvatarsByIdsQuery';
import useSearch from '@hooks/useSearch';
import { MIXPANEL_EVENTS } from '@src/constants/constants';
import { useNetInfo, useOnReconnect } from '@src/context/NetInfoContext';
import { useMixpanel } from '@src/utils/mixpanel';
import { requestChatMarkedRead } from '@utils/chat/chatReadGuard';
import { mapDocumentsToLocalFiles } from '@utils/fileUtils';
import { filterActiveRefetchQueries } from '@utils/filterActiveRefetchQueries';
import { isApolloResourceNotFound } from '@utils/isResourceNotFoundError';
import { logApolloError } from '@utils/logNonFatalError';
import { AsyncStorage } from '@utils/storage';
import {
  clearCachedBlobIds,
  hasRetriedBlobRecovery,
  listExistingLocalFileClientIds,
  markBlobRecoveryRetried,
  planBlobNotFoundRecovery,
} from '@utils/uploadRecovery';

import ChatDetail from './ChatDetail';
import {
  backfillMessageReactions,
  LIST_MESSAGES_REACTIONS_QUERY,
  ReactionBackfillEdge,
} from './chatReactionsBackfill';
import {
  getNewestMessageTimeMs,
  getNewestRealMessageCursor,
  isCaughtUpToRecentActivity,
  isPlaceholderCursor,
} from './messageCursorUtils';
import { MyChatDetail } from '../Chats/MyChatDetail';

const perPageCount = 20;

const firstFetchPolicy = 'cache-and-network';

// listNearbyMessages backs the jump-to-message view (search preview / deep
// links), not the persistent chat feed — unlike listMessages, staleness here
// self-heals the moment the user returns to the normal chat view. Settling
// straight into cache-first (matching the nextFetchPolicy the other two
// queries already fall back to) avoids re-hitting the network on every
// chat-open when the target message's neighborhood is already cached (TA-9951).
const nearbyMessagesFetchPolicy = 'cache-first';

const SUBSCRIPTION_HEARTBEAT_MS = 30_000;

// TA-11079: page size for the live-list walk when exiting search preview.
export const LIVE_OLDER_PAGE_SIZE = 100;

/**
 * How an older-page request ended (TA-11029). The reply jump stops on
 * 'failed' (re-requesting the same cursor would just fail again) and on
 * 'empty' (a page that brought nothing new can't get it any closer).
 * - loaded: the page landed with messages not already loaded
 * - empty: the page landed with nothing new
 * - skipped: nothing was requested (initial query loading, or no next page)
 * - failed: the request errored
 */
export type OlderPageStatus = 'loaded' | 'empty' | 'skipped' | 'failed';

// Restart gating for the debounced/heartbeat subscription restarts (TA-10061).
// restart() throws Apollo invariant 89 when called while the subscription is
// skipped (skip: !activeChatId). The two inputs must be checked independently:
// activeChatId (mirrored into a ref during render) can briefly be truthy while
// Apollo's optionsRef.current.skip is still true during a concurrent render,
// so a truthy activeChatId alone is NOT proof the subscription is active.
export const shouldSkipRestart = (
  activeChatId: string | null | undefined,
  subscriptionSkipped: boolean
): boolean => !activeChatId || subscriptionSkipped;

type Props = {
  chatId?: string;
  messageIdScrollTo?: string;
  messageId?: string;
};

const ChatMessageData: React.FC<Props> = ({
  chatId,
  messageId,
  messageIdScrollTo,
}) => {
  const { isTaskWebPanelOpen } = useWebDrawer();
  const toast = useToast();
  const { trackEvent } = useMixpanel();
  const hasFiredChatOpenedRef = useRef<string | null>(null);
  const appState = useRef(AppState.currentState);
  const [appStateVisible, setAppStateVisible] = useState(appState.current);
  const [messageBeingProcessed, setMessageBeingProcessed] = useState<
    string | null
  >();
  const listDraftDocuments1Ref = useRef<ListDraftDocumentsQuery | undefined>();
  const [refreshing, setRefreshing] = useState(false);
  const [needRefetch, setNeedRefetch] = useState(false);
  const [isInitialMessagesResolved, setIsInitialMessagesResolved] =
    useState(false);
  const needRefetchRef = useRef<boolean>(false);
  const noNeedRefetchRef = useRef<boolean>(false);
  const { checkDocumentURL } = useGetDocumentsByClientIdsQuery();
  const { refreshActivity, setRefreshActivity } = useChatInput();
  const { t } = useTranslation();
  const { isSearchPreview, setIsSearchPreview, markMessageUnavailable } =
    useSearch();

  let showKeyboardFocus;
  // here I add try-catch to fix sonar warning
  try {
    const route = useRoute();
    if (Platform.OS !== 'web') {
      showKeyboardFocus = route?.params?.showKeyboardFocus || false;
    }
  } catch {
    // empty //
  }

  // To be compatible with web, b/c web is relying on the useActiveChat hook
  const {
    activeChatId: aid,
    activeChat,
    activeMessageId,
    setActiveMessageId,
    setActiveChat,
  } = useActiveChat();
  const { id } = activeChat || { id: aid };
  const activeChatId = chatId || id;
  const activeChatIdRef = useRef(activeChatId);
  activeChatIdRef.current = activeChatId;
  // Track skip state via useEffect to match Apollo's optionsRef timing (TA-10061).
  // activeChatIdRef is updated during render and can briefly be non-null from an
  // aborted concurrent render while Apollo's optionsRef.current.skip is still true,
  // causing restart() to throw invariant 89. subscriptionSkippedRef is updated after
  // commit — the same phase Apollo uses — so both checks are in sync.
  const subscriptionSkippedRef = useRef(!activeChatId);
  const fetchNewerMessagesRef = useRef<() => void>(() => {});
  const isFetchingMoreRef = useRef(false);
  // The in-flight older-page fetch, so a caller that hits it can wait for
  // that page instead of getting a silent no-op (TA-11029).
  const fetchMorePromiseRef = useRef<Promise<OlderPageStatus> | null>(null);
  useEffect(() => {
    subscriptionSkippedRef.current = !activeChatId;
    isFetchingMoreRef.current = false;
    fetchMorePromiseRef.current = null;
  }, [activeChatId]);
  const [fetchedNewerMsgId, setFetchedNewerMsgId] = useState<string | null>(
    null
  );
  const [chatIdsToClear, setChatIdsToClear] = useState<string[]>([]);
  const firstPageCount =
    (activeChat?.unreadCount ?? 0) > perPageCount
      ? activeChat?.unreadCount
      : perPageCount;

  const { data: chatData } = useGetChatQuery({
    fetchPolicy: 'cache-first',
    variables: { id: activeChatId as string },
    skip: !activeChatId || Platform.OS !== 'web',
  });
  const { getChat: chat } = (chatData as GetChatQuery) || {};

  useEffect(() => {
    if (Platform.OS === 'web' && chat && !activeChat) {
      setActiveChat(chat);
    }
  }, [chat]);

  // TA-10150: `chat.recentActivityAt` (web) is kept live by the separate,
  // per-user `chatUpdate` subscription (see useChatInput's
  // subscribeToChatUpdates), which reliably fires and updates the far-right
  // rail's unread badge/preview even in cases where reported this chat's
  // dedicated `messageUpdate` subscription below misses an event — the
  // symptom being a badge that increments while the open thread never
  // updates until a manual refresh. useGetChatQuery re-renders whenever
  // chatUpdate's cache.modify touches this chat, so a change here is a
  // reliable "something new landed in this chat" signal. Use it as a
  // backstop to pull the open thread's messages up to date, independent of
  // whether the dedicated per-chat subscription actually delivered.
  // Keyed by chat id so a value carried over from a previously open chat is
  // never compared against the new chat's timestamps — and so no mount-order
  // sensitive "reset on chat switch" effect is needed (resetting from a
  // separate [activeChatId] effect ran after the observe effect below on
  // mount and wiped the first observation, so no change was ever detected).
  const previousRecentActivityAtRef = useRef<{
    chatId: string;
    at: string;
  } | null>(null);
  // Mirror the latest recentActivityAt and live edges in refs so the debounced
  // callback below compares against fresh values at fire time, not the
  // values captured when the debounce was scheduled.
  const latestRecentActivityAtRef = useRef<string | null | undefined>(
    undefined
  );
  latestRecentActivityAtRef.current = chat?.recentActivityAt;
  const latestEdgesRef =
    useRef<Parameters<typeof getNewestMessageTimeMs>[0]>(undefined);
  // TA-10150 QA follow-up: "newest cached message covers recentActivityAt"
  // at debounce-fire time does NOT prove the interval below it is complete.
  // In a burst, message A's messageUpdate can be missed while a later
  // message B (or the user's own send) lands before the 300ms debounce
  // fires — B's timestamp then satisfies the fire-time check and A stays
  // missing until a manual refresh. So behind-ness is recorded at the
  // moment each bump is OBSERVED (before a later message can mask the
  // gap); once set, the flag forces the catch-up fetch regardless of what
  // the cache looks like when the debounce fires.
  const recentActivityCatchUpPendingRef = useRef(false);
  const debouncedRecentActivityCatchUp = useMemo(
    () =>
      debounce(() => {
        // Redundant-fetch guard (TA-10150 follow-up): when no bump was
        // observed while the thread was behind AND the newest cached
        // message covers recentActivityAt, the messageUpdate subscription
        // delivered everything — a catch-up fetch here would only reorder
        // edges and jump the scroll position away from the just-rendered
        // message (QA saw messages "disappear" behind the New Message
        // indicator).
        if (
          !recentActivityCatchUpPendingRef.current &&
          isCaughtUpToRecentActivity(
            latestEdgesRef.current,
            latestRecentActivityAtRef.current
          )
        ) {
          return;
        }
        recentActivityCatchUpPendingRef.current = false;
        fetchNewerMessagesRef.current();
      }, 300),
    []
  );
  useEffect(
    () => () => debouncedRecentActivityCatchUp.cancel(),
    [debouncedRecentActivityCatchUp]
  );
  useEffect(() => {
    if (Platform.OS !== 'web' || !activeChatId || !chat?.recentActivityAt) {
      return;
    }
    const previous = previousRecentActivityAtRef.current;
    previousRecentActivityAtRef.current = {
      chatId: activeChatId,
      at: chat.recentActivityAt,
    };
    // Skip the first observation for this chat (initial load, not a live
    // update) and any re-render carrying the same timestamp.
    if (previous?.chatId !== activeChatId) return;
    if (previous.at === chat.recentActivityAt) return;
    // Behind at observation time means a message this bump announced is not
    // in the cache yet. Record it now: a later message arriving before the
    // debounce fires would otherwise mask the gap (see the ref's comment).
    if (
      !isCaughtUpToRecentActivity(latestEdgesRef.current, chat.recentActivityAt)
    ) {
      recentActivityCatchUpPendingRef.current = true;
    }
    debouncedRecentActivityCatchUp();
  }, [activeChatId, chat?.recentActivityAt, debouncedRecentActivityCatchUp]);

  useEffect(() => {
    setIsInitialMessagesResolved(false);
    setFetchedNewerMsgId(null);
    // A catch-up debounced for the previous chat must not fire after the
    // switch — fetchNewerMessagesRef reads the *current* chat at fire time.
    debouncedRecentActivityCatchUp.cancel();
    // A behind-observation from the previous chat must not force a fetch
    // for the new one.
    recentActivityCatchUpPendingRef.current = false;
  }, [activeChatId, debouncedRecentActivityCatchUp]);

  useEffect(() => {
    if (activeChatId && hasFiredChatOpenedRef.current !== activeChatId) {
      hasFiredChatOpenedRef.current = activeChatId;
      const chatInfo = activeChat ?? chat;
      trackEvent(MIXPANEL_EVENTS.CHAT_OPENED, {
        chat_id: activeChatId,
        chat_type: chatInfo?.isGroupChat ? 'group' : 'direct',
      });
    }
  }, [activeChatId, activeChat, chat]);

  useEffect(() => {
    const clear = async () => {
      await dismissAllNotificationsAsync();
      setChatIdsToClear([]);
    };

    if (chatIdsToClear.length > 0) {
      clear();
    }
  }, [chatIdsToClear]);

  // TA-11078: native's target is the route param, which the handler below
  // can't clear. A target found to be deleted is ignored locally instead:
  // the nearby query skips, its error clears, and the thread shows its own
  // empty/loaded state rather than the load-failure one. Any other target
  // (a later deep link, a notification) is unaffected.
  const [unavailableTargetId, setUnavailableTargetId] = useState<string>();
  const requestedMessageId = messageIdScrollTo ?? activeMessageId;
  const xMessageId =
    requestedMessageId === unavailableTargetId ? undefined : requestedMessageId;

  const sortMessages = (
    a?: Pick<Message, 'publishedAt' | 'createdAt'> | null,
    b?: Pick<Message, 'publishedAt' | 'createdAt'> | null
  ) => {
    if (!a && !!b) return -1;
    if (!b && !!a) return 1;
    if (!a && !b) return 0;
    return new Date(a?.publishedAt || a?.createdAt) <
      new Date(b?.publishedAt || b?.createdAt)
      ? 1
      : -1;
  };

  const [markChatsRead] = useMarkChatsReadMutation();

  const {
    data: listMessagesData,
    fetchMore,
    loading,
    refetch,
    error: listMessagesError,
  } = useListMessagesQuery({
    fetchPolicy: firstFetchPolicy,
    nextFetchPolicy: 'cache-first',
    // TA-10379: an incomplete cache diff (e.g. a Document still attaching
    // async — see Message.attachments merge in initializeCache.ts) made
    // 'cache-first' skip the cache and fetch page 1 bare, visibly resetting
    // pagination. returnPartialData keeps the deep cached data rendering
    // immediately instead; the bare-request guard in merge() protects it
    // once that network response lands.
    returnPartialData: true,
    variables: {
      chatId: activeChatId as string,
      first: firstPageCount,
    },
    skip: !activeChatId,
  });

  useEffect(() => {
    // returnPartialData can deliver a truthy listMessagesData whose
    // `listMessages` key is itself still undefined (the connection hasn't
    // resolved yet) — gate on the actual payload, not just the wrapper.
    if (!listMessagesData?.listMessages) return;
    setIsInitialMessagesResolved(true);
    if (Platform.OS !== 'web' && activeChatId) {
      setChatIdsToClear((prev) => [...prev, activeChatId!]);
    }
  }, [listMessagesData]);

  useEffect(() => {
    if (!listMessagesError) return;
    setIsInitialMessagesResolved(true);
    if (refreshing) setRefreshing(false);
  }, [listMessagesError]);

  const { data: deletedMessageIdsData } = useListDeletedMessageIdsQuery({
    fetchPolicy: firstFetchPolicy,
    nextFetchPolicy: 'cache-first',
    variables: {
      chatId: activeChatId as string,
    },
    skip: !activeChatId,
  });

  const shouldSkipNearby = !xMessageId || !activeChatId;

  const {
    data: listNearbyMessagesData,
    loading: nearbyLoading,
    error: nearbyMessagesError,
  } = useListNearbyMessagesQuery({
    fetchPolicy: nearbyMessagesFetchPolicy,
    nextFetchPolicy: nearbyMessagesFetchPolicy,
    variables: {
      messageId: xMessageId as string,
      nearbyCount: 30,
    },
    skip: shouldSkipNearby,
  });

  useEffect(() => {
    if (!listNearbyMessagesData) return;
    if (refreshing) setRefreshing(false);
    if (Platform.OS !== 'web' && activeChatId) {
      setChatIdsToClear((prev) => [...prev, activeChatId!]);
    }
  }, [listNearbyMessagesData]);

  useEffect(() => {
    if (!nearbyMessagesError) return;
    if (refreshing) setRefreshing(false);
  }, [nearbyMessagesError]);

  // TA-11078: a search result whose message was deleted after indexing
  // answers "Message not found". Say so, drop the dead result and leave
  // preview instead of stranding the user in an empty one. Its own effect,
  // without `refreshing`, so a retained error can't cancel a pull-to-refresh.
  // The error object Apollo retains between renders is handled once.
  const handledNearbyErrorRef = useRef<unknown>(undefined);
  useEffect(() => {
    if (!nearbyMessagesError) {
      handledNearbyErrorRef.current = undefined;
      return;
    }
    if (
      !isSearchPreview ||
      !xMessageId ||
      handledNearbyErrorRef.current === nearbyMessagesError ||
      !isApolloResourceNotFound(nearbyMessagesError)
    )
      return;
    handledNearbyErrorRef.current = nearbyMessagesError;
    toast.show(t('models:chat.searchPreview.unavailable'), {
      duration: 4000,
    });
    markMessageUnavailable(xMessageId);
    setUnavailableTargetId(xMessageId);
    setIsSearchPreview(false);
    setActiveMessageId?.(undefined);
  }, [
    nearbyMessagesError,
    isSearchPreview,
    xMessageId,
    toast,
    t,
    markMessageUnavailable,
    setIsSearchPreview,
    setActiveMessageId,
  ]);

  // Mirrors debouncedUpdateChatsRead's 500ms lodash debounce below — rapid
  // chat switching (e.g. arrow-keying through the chat list) must not fire
  // one markChatsRead request per chat touched (TA-9951, Sentry Consecutive
  // HTTP). But every chat the user opened — however briefly — must still be
  // marked read: cancelling the pending call on a quick exit left the unread
  // badge stuck (TA-10214), and the TA-10151 snapshot patch had already
  // zeroed the badge locally, so it silently resurrected on the next refetch.
  // Accumulate the opened chat ids and send them as one batched mutation
  // when the user settles; on close/unmount the pending call is flushed,
  // never dropped.
  const pendingMarkReadChatIdsRef = useRef<Set<string>>(new Set());
  const debouncedMarkChatsReadOnOpen = useMemo(
    () =>
      debounce(() => {
        const chatIds = Array.from(pendingMarkReadChatIdsRef.current);
        pendingMarkReadChatIdsRef.current.clear();
        if (chatIds.length === 0) return;
        const runMarkRead = () =>
          markChatsRead({
            variables: { attributes: { chatIds } },
          });
        // TA-9295: the single-chat case — opening one chat and sending in it
        // — goes through the shared coalescer so the open, the send and the
        // messageUpdate echo cannot stack up into three overlapping requests
        // for the same chat (TA-9951). A multi-chat batch stays a single
        // request of its own; the coalescer is keyed per chat.
        if (chatIds.length === 1) {
          requestChatMarkedRead(chatIds[0], runMarkRead);
          return;
        }
        // A multi-chat batch goes out unguarded on purpose: it only forms
        // when the user arrow-keys/swipes through several chats inside the
        // 500ms debounce, i.e. without sending, so there is no own-echo
        // chatUpdate racing its receipt. Those chats therefore get no
        // shouldIgnoreUnreadUpdate window — acceptable, since the race the
        // guard exists for is a send-triggered one.
        runMarkRead();
      }, 500),
    []
  );

  useEffect(() => {
    if (activeChatId) {
      pendingMarkReadChatIdsRef.current.add(activeChatId);
      debouncedMarkChatsReadOnOpen();
    } else {
      // activeChatId went from truthy to falsy while this component stayed
      // mounted (e.g. closing the chat panel on web). The user did open the
      // chat, so the pending mark-as-read must fire now rather than be
      // cancelled — exiting within the 500ms window must still clear the
      // unread badge (TA-10214). flush() is a no-op when nothing is pending.
      debouncedMarkChatsReadOnOpen.flush();
    }
  }, [activeChatId]);

  useEffect(() => () => debouncedMarkChatsReadOnOpen.flush(), []);

  // Connectivity comes from the shared NetInfo provider, never from a raw
  // NetInfo.addEventListener here: this component is mounted app-wide via
  // ChatDrawer.web.tsx, so a second subscriber reopens the TA-9366 cascade
  // on every screen (TA-10662, Sentry FRONTEND-2Z9).
  useOnReconnect(() => {
    // graphql-ws (retryAttempts: 100) auto-reconnects subscriptions when
    // the network comes back — calling restart() here races with its own
    // cache writes and triggers invariant 89. Use reverse pagination
    // (last/before) instead of refetch() so that pageInfo.endCursor is
    // preserved when the user has paginated into older history (TA-10379).
    // Consistent with the heartbeat path (line ~786).
    fetchNewerMessagesRef.current();
  });

  const { listMessages } = listMessagesData || {};
  const { listNearbyMessages } = listNearbyMessagesData || {};

  const { edges, pageInfo } = isSearchPreview
    ? listNearbyMessages || { edges: [], pageInfo: null }
    : listMessages || { edges: [], pageInfo: null };

  // TA-11079: the live thread's edges/pageInfo regardless of preview, so
  // ChatDetail can page the live list toward the previewed message while the
  // preview stays on screen.
  const liveEdges = listMessages?.edges;
  const livePageInfo = listMessages?.pageInfo;
  // TA-11115: the recentActivityAt catch-up asks whether the LIVE list is
  // behind. In preview `edges` is the old nearby window, which would always
  // look behind and force a fetch on every bump.
  latestEdgesRef.current = liveEdges;
  const liveMessageIds = useMemo(
    () => new Set((liveEdges ?? []).map((edge) => edge?.node?.id)),
    [liveEdges]
  );
  const isInLiveList = useCallback(
    (id: string) => liveMessageIds.has(id),
    [liveMessageIds]
  );

  const { client } = useApolloClient();

  useEffect(() => {
    const listDeletedMessageIds = deletedMessageIdsData?.listDeletedMessageIds;
    if (!listDeletedMessageIds?.length) return;
    client?.cache.modify({
      fields: {
        listMessages(existing, { readField }) {
          if (!existing?.edges) return existing;
          const newEdges = existing.edges.filter(
            (edge: any) =>
              !listDeletedMessageIds.includes(readField('id', edge.node))
          );
          return { ...existing, edges: newEdges };
        },
      },
    });
  }, [deletedMessageIdsData, client]);

  const onRefresh = useCallback(() => {
    setRefreshing(true);
    refetch();
  }, []);

  // NOTE: null is returned when initially subscribing to the messageCreated subscription.
  // graphql-ruby returns :no_response by default and Apollo expects a value.
  const sortChatMessageEdges = (edges: MessageEdge[] | null) => {
    const publishedMessages = (edges || [])
      .filter((item): item is MessageEdge => !!item)
      .map((item) => item.node);
    const messages: Message[] = publishedMessages
      .filter((item): item is Message => !!item?.clientId)
      .sort(sortMessages);
    return messages;
  };

  const messages = useMemo(() => {
    return sortChatMessageEdges((edges || []) as MessageEdge[]);
  }, [edges]);

  const { data: listDraftMessagesData } = useListDraftMessagesQuery({
    fetchPolicy: 'cache-only',
  });

  const allMessages = useMemo(() => {
    const rawDrafts = listDraftMessagesData?.listDraftMessages;
    const allDrafts = Array.isArray(rawDrafts) ? rawDrafts : [];
    if (allDrafts.length === 0) return messages;
    const messageClientIds = new Set(messages.map((m) => m.clientId));
    const pendingDrafts = allDrafts.filter(
      (draft) =>
        draft?.chatId === activeChatId &&
        !messageClientIds.has(draft?.clientId ?? '')
    ) as Message[];
    if (pendingDrafts.length === 0) return messages;
    return [...messages, ...pendingDrafts].sort(sortMessages);
  }, [listDraftMessagesData?.listDraftMessages, messages, activeChatId]);

  // TA-10162: retire a sent draft's listDraftMessages entry only once the
  // listMessages HOOK actually delivers the published message. Removing it
  // from a mutation callback instead (create/update onCompleted) races the
  // hook's broadcast — the draft echo (the message's only render source
  // while the listMessages read is incomplete) disappears a beat before the
  // confirmed message renders, blanking the message out of the thread.
  // The allMessages dedupe above hides the draft as soon as `messages`
  // contains its clientId, so this cleanup can never cause a visual change —
  // it only stops the sent draft from leaking into the list forever.
  useEffect(() => {
    const rawDrafts = listDraftMessagesData?.listDraftMessages;
    if (!client || !Array.isArray(rawDrafts) || rawDrafts.length === 0) return;
    const publishedClientIds = new Set(
      messages.filter((m) => m.publishedAt).map((m) => m.clientId)
    );
    rawDrafts.forEach((draft) => {
      if (draft?.clientId && publishedClientIds.has(draft.clientId)) {
        removeDraftMessageEntry(client.cache, draft.clientId);
      }
    });
  }, [listDraftMessagesData?.listDraftMessages, messages, client]);

  const { checkUserAvatarURLByIds } = useGetUserAvatarsByIdsQuery();

  // Stable primitive derived from the actual set of author avatar IDs.
  // Using a sorted, deduplicated join means the effect only fires when the
  // set of IDs truly changes — not on every subscription update — while still
  // allowing the hook's built-in expiry logic to re-validate previously seen URLs.
  const avatarIdsString = useMemo(
    () =>
      [
        ...new Set(
          messages
            .filter((t) => t.authorAvatarId)
            .map((t) => t.authorAvatarId as string)
        ),
      ]
        .sort((a, b) => a.localeCompare(b))
        .join(','),
    [messages]
  );

  useEffect(() => {
    if (!avatarIdsString) return;
    checkUserAvatarURLByIds(avatarIdsString.split(','));
  }, [avatarIdsString]);

  const fetchUrl = () => {
    const xfiles: Document[] = [];
    listMessagesData?.listMessages?.edges?.forEach((m) => {
      const files = m?.node?.attachments?.filter(
        (t) => t?.__typename === 'Document' && t.clientId
      );
      if ((files?.length ?? 0) > 0) {
        xfiles.push(...files);
      }
      const files2 = m?.node?.replyMessage?.attachments?.filter(
        (t) => t?.__typename === 'Document' && t.clientId
      );
      if ((files2?.length ?? 0) > 0) {
        xfiles.push(...files2);
      }
    });
    if (xfiles.length > 0) {
      checkDocumentURL(xfiles);
    }
  };
  useEffect(() => {
    fetchUrl();
  }, [listMessagesData]);

  const { data: listDraftDocuments } = useListDraftDocumentsQuery({
    variables: {
      chatId: activeChatId || '',
    },
    fetchPolicy: 'cache-only',
    skip: !activeChatId,
  });

  const { restart } = useMessageUpdateSubscription({
    variables: { chatId: activeChatId as string },
    skip: !activeChatId,
    onData: ({ data: subscriptionData, client }) => {
      const newMessageNode = subscriptionData.data?.messageUpdate;
      newMessageNode?.attachments?.forEach((a) => {
        if (a?.__typename === 'Document' && a.file?.url) {
          const af = a as Document;
          AsyncStorage.setItem(af.clientId, af.file.url);
        }
      });
      if (!newMessageNode) return;
      const { cache } = client;

      cache.updateFragment(
        {
          id: cache.identify(newMessageNode),
          fragment: MessageUpdateLiteFragmentDoc,
          fragmentName: 'MessageUpdateLite',
        },
        (data) => {
          if (!data) {
            return { ...newMessageNode, failedAt: null };
          }
          return { ...data, ...newMessageNode, failedAt: null };
        }
      );

      cache.updateQuery(
        {
          query: ListMessagesDocument,
          variables: {
            chatId: activeChatId,
          },
        },
        (data) => {
          if (!data) return;

          const { listMessages }: { listMessages: MessageConnection } = data;
          const newMessageIdentity = cache.identify(newMessageNode);
          const edges = listMessages.edges || [];

          // Already confirmed by cache identity — nothing to do
          if (
            edges.some((item) => {
              if (!item?.node) return false;
              return cache.identify(item.node) === newMessageIdentity;
            })
          )
            return undefined;

          // Optimistic entry present (same clientId, different UUID id) — replace in-place
          // to avoid a duplicate flash when the server-confirmed message arrives
          const hasOptimistic = edges.some(
            (item) => item?.node?.clientId === newMessageNode.clientId
          );
          if (hasOptimistic) {
            return {
              listMessages: {
                ...listMessages,
                edges: edges.map((item) => {
                  if (item?.node?.clientId === newMessageNode.clientId) {
                    return { ...item, node: newMessageNode };
                  }
                  return item;
                }),
              },
            };
          }

          return {
            listMessages: {
              ...listMessages,
              edges: edges.concat({
                cursor: uuid.v4().toString(),
                node: newMessageNode,
              }),
            },
          };
        }
      );

      if (newMessageNode.chatId) {
        debouncedUpdateChatsRead(newMessageNode.chatId);
      }
    },
  });

  const { restart: restartMessageDeleteSubscription } =
    useMessageDeleteSubscription({
      variables: { chatId: activeChatId as string },
      skip: !activeChatId,
      onError: (error) => {
        // Suppress silently — the ErrorLink already downgrades this to a
        // Sentry warning. Logging here too would double-report the incident.
        const isNullCreatedAt = error.graphQLErrors.some(
          (e) =>
            e.path?.[0] === 'messageDelete' &&
            e.message.includes(
              'Cannot return null for non-nullable field Message.createdAt'
            )
        );
        if (isNullCreatedAt) {
          return;
        }
      },
      onData: ({ data: subscriptionData, client }) => {
        const deletedMessage = subscriptionData.data?.messageDelete;
        if (!deletedMessage) return;

        const deletedMessageId = deletedMessage.id;

        client.cache.modify({
          fields: {
            listMessages(existing, { readField }) {
              if (!existing?.edges) return existing;

              const newEdges = existing.edges.filter(
                (edge: any) => readField('id', edge.node) !== deletedMessageId
              );

              return {
                ...existing,
                edges: newEdges,
              };
            },
          },
        });
      },
    });

  // TA-10153: reaction changes land on EXISTING messages, so a missed
  // messageUpdate event is never recovered by fetchNewerMessages (which only
  // backfills newer messages) or by a subscription restart (which only
  // re-subscribes). Re-read a reaction-lite page alongside every restart and
  // write the reaction fields directly into the cache. fetchPolicy no-cache
  // keeps the result out of the Query.listMessages merge (no pageInfo reset).
  const refreshReactions = useCallback(() => {
    const chatIdNow = activeChatIdRef.current;
    if (!chatIdNow || !client) return;
    client
      .query({
        query: LIST_MESSAGES_REACTIONS_QUERY,
        // firstPageCount, not perPageCount: the initial listMessages load can
        // exceed one page when unreadCount > perPageCount, and the backfill
        // must cover the same window the user has loaded.
        variables: { chatId: chatIdNow, first: firstPageCount },
        fetchPolicy: 'no-cache',
      })
      .then(({ data }) => {
        // Chat switched while the request was in flight — drop the result.
        if (activeChatIdRef.current !== chatIdNow) return;
        const edges = data?.listMessages?.edges as
          | ReactionBackfillEdge[]
          | undefined;
        backfillMessageReactions(client.cache, edges);
      })
      .catch(() => {
        // Non-fatal: the next heartbeat tick retries.
      });
  }, [client, firstPageCount]);
  const refreshReactionsRef = useRef(refreshReactions);
  refreshReactionsRef.current = refreshReactions;

  const debouncedRestartAll = useMemo(
    () =>
      debounce(() => {
        // Use refs so the debounced closure always sees the current values;
        // see shouldSkipRestart for why both conditions are required.
        if (
          shouldSkipRestart(
            activeChatIdRef.current,
            subscriptionSkippedRef.current
          )
        )
          return;
        restart();
        restartMessageDeleteSubscription();
        // Use fetchNewerMessages (last/before startCursor) instead of refetch():
        // fetches only messages newer than the cache rather than re-fetching page 1,
        // so pageInfo is preserved and users reading older messages won't see jumps.
        fetchNewerMessagesRef.current();
        refreshReactionsRef.current();
      }, 200),
    [] // restart/restartMessageDeleteSubscription/fetchNewerMessagesRef are stable refs
  );

  useEffect(() => () => debouncedRestartAll.cancel(), []);

  const updateChatsRead = (chatId: string) => {
    // TA-9295: shares the send path's coalescer — a message arriving right
    // after the user sent one would otherwise fire a second, overlapping
    // markChatsRead for the same chat.
    requestChatMarkedRead(chatId, () =>
      markChatsRead({
        variables: {
          attributes: {
            chatIds: [chatId],
          },
        },
        onCompleted: () => {
          if (
            listDraftDocuments1Ref.current &&
            listDraftDocuments1Ref.current?.listDraftDocuments.length > 0
          ) {
            setNeedRefetch(true);
            needRefetchRef.current = true;
          } else if (!needRefetchRef.current) {
            setTimeout(() => {
              if (noNeedRefetchRef.current) {
                noNeedRefetchRef.current = false;
              } else {
                // Use reverse pagination (last/before) rather than refetch() so
                // that pageInfo.endCursor is preserved when the user has already
                // paginated into older history (TA-10379).
                fetchNewerMessagesRef.current();
              }
            }, 3000);
          }
        },
      })
    );
  };

  useEffect(() => {
    if (
      needRefetch &&
      needRefetchRef.current &&
      // Use the live query data rather than the ref (which is only updated in
      // a separate effect after render and never triggers a re-run here).
      listDraftDocuments?.listDraftDocuments.length === 0
    ) {
      // Use reverse pagination (last/before) rather than refetch() so that
      // pageInfo.endCursor is preserved when the user has already paginated
      // into older history (TA-10379). Same fix as the markChatsRead timeout.
      fetchNewerMessagesRef.current();
      setNeedRefetch(false);
      needRefetchRef.current = false;
    }
  }, [needRefetch, listDraftDocuments]);

  const debouncedUpdateChatsRead = useMemo(
    () => debounce(updateChatsRead, 500),
    []
  );

  const [updateMessageIndividual] = useUpdateMessageIndividualMutation();

  const { isConnected } = useNetInfo();
  const {
    finalizeUploadedFiles,
    uploadingFiles,
    directUploadFiles,
    invalidateUploadedBlob,
  } = useChatUploadManager();

  useEffect(() => {
    listDraftDocuments1Ref.current = listDraftDocuments;
    if (isConnected) {
      uploadDocument();
    }
  }, [listDraftDocuments, isConnected]);

  /**
   * TA-10885: the terminal BLOB_NOT_FOUND path — the file really is gone, so
   * the message can never publish. Unchanged behaviour from TA-10062, lifted
   * out so the recovery path above can read.
   */
  const retireUnsendableMessage = (message: Message, documents: Document[]) => {
    toast.show('The file has been deleted and cannot be sent', {
      duration: 4000,
    });
    // Clear the draft docs from cache so uploadDocument does not
    // retry on remount or reconnect (the blob can never be found).
    const storedId = 'MsgClientID' + message.clientId;
    void AsyncStorage.deleteItem(storedId);
    // The message can never publish — retire its draft entry too,
    // or it lingers in listDraftMessages forever (TA-10162).
    if (client) {
      removeDraftMessageEntry(client.cache, message.clientId);
    }
    client?.cache.updateQuery(
      {
        query: ListDraftDocumentsDocument,
        // activeChatId is always truthy here — uploadDocument only runs
        // when useListDraftDocumentsQuery (skip: !activeChatId) has data.
        variables: { chatId: activeChatId },
      },
      (cacheData) => ({
        listDraftDocuments: (cacheData?.listDraftDocuments || []).filter(
          // Scope to the current message only — documents spans all
          // pending messages and using it directly would clear draft
          // docs that belong to other unrelated messages.
          (d: Document) => d.messageClientId !== message.clientId
        ),
      })
    );
    finalizeUploadedFiles(mapDocumentsToLocalFiles(documents));
  };

  /**
   * TA-10885: BLOB_NOT_FOUND does not prove the user's file is gone. The
   * upload manager dedupes by content hash against a blobId map persisted
   * across launches, and offline that map is the only way a blobId can appear
   * at all — nothing can upload — so an attachment queued in airplane mode is
   * routinely marked uploaded against a blob the server has since dropped.
   * Retiring it there loses a file that is still on the device, and the
   * created-but-unpublished message spins forever.
   *
   * So: if every file is still on disk, the blobId is stale, not the file.
   * Forget it and let the uploader run again — clearing the cached blobId
   * re-broadcasts listDraftDocuments, which re-enters uploadDocument with the
   * documents pending once more. One retry only (see planBlobNotFoundRecovery).
   */
  const handleBlobNotFound = async (
    message: Message,
    sentDocuments: Document[],
    retire: () => void
  ) => {
    const files = mapDocumentsToLocalFiles(sentDocuments);
    const plan = planBlobNotFoundRecovery({
      files,
      existingClientIds: await listExistingLocalFileClientIds(files),
      alreadyRetried: hasRetriedBlobRecovery(message.clientId),
    });

    if (plan.action === 'retire') {
      retire();
      return;
    }

    markBlobRecoveryRetried(message.clientId);
    await invalidateUploadedBlob(plan.files);
    if (client) {
      clearCachedBlobIds(client.cache, plan.files);
    }
  };

  const uploadDocument = async () => {
    const documents: Document[] = (
      listDraftDocuments?.listDraftDocuments || []
    ).filter(
      (doc: Pick<Document, 'id' | 'clientId' | 'messageCreateYn'>) =>
        doc.id == doc.clientId && doc.messageCreateYn
    );

    if (documents.length < 1) {
      return;
    }

    const documentMap = new Map(
      documents.map((doc) => [doc.messageClientId, doc])
    ); // Create a map for quick lookup

    const filteredMessage: Message[] = messages.filter((msg) =>
      documentMap.has(msg?.clientId)
    ); // Filter messages with a matching clientId
    const message = filteredMessage[0]; // Get the first matched message

    if (!message) {
      // TODO handle removing this doc from the query
      return;
    }

    if (messageBeingProcessed === message.clientId) return;

    const uploadingMap = new Map(
      uploadingFiles.map((f) => [f.file.clientId, f])
    );
    const documentsToProcess = documents.filter((doc) => {
      if (doc.messageClientId !== message.clientId) return false;

      const uploadInfo = uploadingMap.get(doc.clientId);

      const hasBlobId =
        (doc.blobId && doc.blobId !== '') ||
        (uploadInfo?.status === 'success' && uploadInfo.file.blobId);

      return !hasBlobId;
    });
    if (documentsToProcess.length > 0) {
      const uploadingClientIds = new Set(
        uploadingFiles
          .filter((f) => f.status === 'uploading')
          .map((f) => f.file.clientId)
      );

      const localFilesToUpload: LocalFile[] = mapDocumentsToLocalFiles(
        documentsToProcess.filter(
          (doc) => !uploadingClientIds.has(doc.clientId)
        )
      );

      if (localFilesToUpload.length > 0) {
        try {
          await directUploadFiles(localFilesToUpload);
        } catch (err) {
          console.error('Auto reupload failed files error:', err);
          return;
        }
      }
      return;
    }

    const documentsProcessed = documents
      .map((doc) => {
        if (doc.messageClientId !== message.clientId) return doc;

        const uploadInfo = uploadingMap.get(doc.clientId);

        const shouldUpdateBlobId =
          (!doc.blobId || doc.blobId === '') &&
          uploadInfo?.status === 'success' &&
          uploadInfo.file.blobId;

        if (shouldUpdateBlobId) {
          return {
            ...doc,
            blobId: uploadInfo.file.blobId,
          };
        }

        return doc;
      })
      .filter((doc) => {
        return (
          doc.messageClientId === message.clientId &&
          doc.blobId &&
          doc.blobId !== ''
        );
      });

    const messageProjects = (message.attachments || []).filter(
      (item) => item.__typename === 'MessageProject'
    ) as MessageProject[];

    const messageTasks = (message.attachments || []).filter(
      (item) => item.__typename === 'MessageTask'
    ) as MessageTask[];

    setMessageBeingProcessed(message.clientId);

    try {
      const documentAttributes = documentsProcessed.map((item) => {
        const {
          contentType,
          clientId,
          name,
          isImage,
          isAudio,
          thumbnail,
          duration,
          blobId,
        } = item;

        return {
          name,
          contentType,
          blobId,
          isImage,
          isAudio,
          duration,
          clientId,
          thumbnail,
          projectIds: messageProjects.map((item) => item.project?.id),
          taskIds: messageTasks.map((item) => item.task?.id),
        } as DocumentAttributes;
      });
      const projectTags = messageProjects.map((item) => ({
        projectId: item?.project?.id || '',
        authorId: item.authorId,
      }));
      const taskTags = messageTasks.map((item) => ({
        taskId: item?.task?.id || '',
        authorId: item.authorId,
      }));
      const refetchQueries: string[] = [];
      if (projectTags.length > 0 && Platform.OS === 'web') {
        if (window.location.href?.includes('/globalActivity')) {
          refetchQueries.push('listActivities');
        }
        if (window.location.href?.includes('/projects/')) {
          refetchQueries.push(
            'listProjectDocuments',
            'listActivitiesByProject'
          );
        }

        if (isTaskWebPanelOpen && taskTags.length > 0) {
          refetchQueries.push('listTaskDocuments', 'listActivitiesByTask');
        }
      } else if (projectTags.length > 0 || taskTags.length > 0) {
        refetchQueries.push('listActivities');
      }
      // TA-10233: string refetch names only match active observable queries;
      // inactive names (e.g. task panel open but its queries skipped) trigger
      // Apollo unknown-query warnings without refetching anything.
      const activeRefetchQueries = filterActiveRefetchQueries(
        client,
        refetchQueries
      );
      await updateMessageIndividual({
        variables: {
          clientId: message.clientId,
          attributes: {
            chatId: message.chatId,
            documents: documentAttributes,
            publish: true,
            messageProjects: projectTags,
            messageTasks: taskTags,
          },
        },
        refetchQueries: activeRefetchQueries,
        onCompleted: (data) => {
          const storedId =
            'MsgClientID' + data.updateMessageIndividual.clientId;
          AsyncStorage.deleteItem(storedId);

          // TA-10162: land the confirmed published message in listMessages
          // BEFORE the draft documents are removed below — otherwise the
          // attachment message vanishes until the messageUpdate subscription
          // or refetch catches up (same flicker as the plain-text path).
          if (client) {
            insertPublishedMessageEdge(
              client.cache,
              data.updateMessageIndividual
            );
          }
          // The draft entry in listDraftMessages is NOT removed here — the
          // retirement effect above waits until the listMessages hook
          // actually delivers the published message, so the echo never
          // disappears before its replacement renders (TA-10162).

          if (refetchQueries.find((item) => item === 'listActivities')) {
            setTimeout(() => {
              !refreshActivity && setRefreshActivity(true);
            }, 500);
          }

          client?.cache.updateQuery(
            {
              query: ListDraftDocumentsDocument,
              variables: {
                chatId: activeChatId,
              },
            },
            (data) => {
              return {
                listDraftDocuments: [
                  ...(data?.listDraftDocuments || []).filter(
                    (d: Document) =>
                      !documentsToProcess.some(
                        (fd) => d.clientId === fd.clientId
                      )
                  ),
                ],
              };
            }
          );
          finalizeUploadedFiles(mapDocumentsToLocalFiles(documents));
        },
        onError: (err) => {
          if (
            err.graphQLErrors.some(
              (e) => e?.extensions?.code === 'BLOB_NOT_FOUND'
            )
          ) {
            void handleBlobNotFound(message, documentsProcessed, () =>
              retireUnsendableMessage(message, documents)
            );
            return;
          }
          Sentry.captureException(
            new Error('updateMessageIndividual error: ' + JSON.stringify(err))
          );
        },
      });
    } catch (err) {
      console.error('Upload failed:', err);
      client?.cache.updateFragment<MessageDetailFragment>(
        {
          id: client.cache.identify({
            ...message,
            __typename: 'Message',
          }),
          fragment: MessageDetailFragmentDoc,
          fragmentName: 'MessageDetail',
        },
        (data: any) => {
          return {
            ...data,
            failedAt: new Date().toISOString(),
          };
        }
      );
    } finally {
      setMessageBeingProcessed(null);
    }
  };

  // One older-page request (listMessages fetchMore) with the shared in-flight
  // guard; used by fetchAfterCursor and fetchLiveOlderPage (TA-11079).
  const requestOlderPage = useCallback(
    (
      variables: Record<string, unknown>,
      loadedIds: Set<string | undefined>,
      logLabel: string,
      pageSize: number
    ): Promise<OlderPageStatus> => {
      isFetchingMoreRef.current = true;
      const request = fetchMore({ variables })
        .then((fetchMoreResult): OlderPageStatus => {
          const newEdges = fetchMoreResult.data?.listMessages?.edges ?? [];

          const documents = newEdges
            .flatMap((edge) => edge?.node?.attachments ?? [])
            .filter((att): att is Document => att?.__typename === 'Document');

          if (documents.length > 0) {
            checkDocumentURL(documents);
          }
          return newEdges.some((edge) => !loadedIds.has(edge?.node?.id))
            ? 'loaded'
            : 'empty';
        })
        .catch((error: unknown): OlderPageStatus => {
          // Reported, not thrown: the infinite-scroll caller ignores the
          // result, and the reply jump stops on 'failed' (TA-11029).
          if (error instanceof ApolloError) {
            logApolloError(error, logLabel, [
              { chatId: activeChatId, pageSize },
            ]);
          }
          return 'failed';
        })
        .finally(() => {
          isFetchingMoreRef.current = false;
          fetchMorePromiseRef.current = null;
        });
      fetchMorePromiseRef.current = request;
      return request;
    },
    [activeChatId, fetchMore, checkDocumentURL]
  );

  // Resolves once the requested page has landed — or, if a page is already
  // in flight, once that one has — with how it went (OlderPageStatus).
  // TA-11029: the reply jump walks back with this and decides on the status;
  // a bare no-op here left it waiting forever. While the initial query is
  // loading it resolves 'skipped' at once; callers that need to retry then
  // should re-run when `loading` settles.
  const fetchAfterCursor = useCallback(
    (pageSize = perPageCount) => {
      if (isFetchingMoreRef.current && fetchMorePromiseRef.current) {
        return fetchMorePromiseRef.current;
      }
      if (loading || !pageInfo?.hasNextPage || isFetchingMoreRef.current) {
        return Promise.resolve<OlderPageStatus>('skipped');
      }
      const loadedIds = new Set(edges?.map((edge) => edge?.node?.id));

      let xcursor = pageInfo.endCursor;
      const rtn = edges?.filter(
        (n) => n?.node?.id === messages[messages.length - 1].id
      );
      // when there is cache data, the pageinfo's end cursor is not right, so to do reset here.
      if (
        (rtn?.length ?? 0) > 0 &&
        rtn![0]!.cursor &&
        xcursor !== rtn![0]!.cursor &&
        !isPlaceholderCursor(rtn![0]!.cursor)
      ) {
        xcursor = rtn![0]!.cursor;
      }

      const variables: any = {
        chatId: activeChatId,
        first: pageSize,
      };
      if (xcursor) {
        variables.after = xcursor;
      }

      return requestOlderPage(
        variables,
        loadedIds,
        'ChatMessageData.fetchAfterCursor',
        pageSize
      );
    },
    [edges, pageInfo, activeChatId, loading, perPageCount, requestOlderPage]
  );

  // TA-11079: one older page of the LIVE list (listMessages), whether or not
  // the search preview is showing. Same shared in-flight guard as
  // fetchAfterCursor; resolves 'skipped' while the initial query is loading or
  // when there is no older page.
  const fetchLiveOlderPage = useCallback(
    (pageSize = LIVE_OLDER_PAGE_SIZE): Promise<OlderPageStatus> => {
      if (isFetchingMoreRef.current && fetchMorePromiseRef.current) {
        return fetchMorePromiseRef.current;
      }
      if (loading || !livePageInfo?.hasNextPage || isFetchingMoreRef.current) {
        return Promise.resolve('skipped');
      }
      // pageInfo.endCursor can lag the cache; anchor on the oldest real edge.
      const oldestEdge = (liveEdges ?? [])
        .filter((edge) => !!edge?.node?.clientId)
        .sort((a, b) => sortMessages(a?.node, b?.node))
        .pop();
      const after =
        oldestEdge?.cursor && !isPlaceholderCursor(oldestEdge.cursor)
          ? oldestEdge.cursor
          : livePageInfo.endCursor;
      const loadedIds = new Set(liveEdges?.map((edge) => edge?.node?.id));

      return requestOlderPage(
        { chatId: activeChatId, first: pageSize, after },
        loadedIds,
        'ChatMessageData.fetchLiveOlderPage',
        pageSize
      );
    },
    [liveEdges, livePageInfo, activeChatId, loading, requestOlderPage]
  );

  // Fetch only messages newer than what is already in the cache, using reverse
  // pagination (last/before startCursor). This is used on focus/AppState-active
  // instead of refetch() to avoid resetting pageInfo and causing scroll jumps
  // when the user has paginated into older messages.
  // Falls back to refetch() when startCursor is unavailable (e.g. initial load).
  const fetchNewerMessages = useCallback(() => {
    // TA-10150: anchor the reverse fetch on the newest message that carries a
    // REAL server cursor instead of trusting pageInfo.startCursor. The stored
    // startCursor is unreliable in two ways: the custom listMessages merge
    // (initializeCache) lets a forward older-page fetch overwrite it with that
    // older batch's startCursor, and edges inserted by the messageUpdate
    // subscription carry synthetic uuid cursors (see isPlaceholderCursor).
    // A `before` anchored on a stale or synthetic cursor makes the backend
    // return a mid-history slice — or nothing — so the truly new messages
    // never land and the open thread stays stale until a manual refresh.
    //
    // TA-11115: always anchor on the LIVE listMessages edges, even in search
    // preview. This fetchMore merges into listMessages, but in preview `edges`
    // is the listNearbyMessages window, whose cursors are array indexes
    // (Base64("60")) that listMessages reads as legacy offsets from the
    // newest message — an unrelated slice got merged, leaving a gap.
    const before =
      getNewestRealMessageCursor(liveEdges) ?? livePageInfo?.startCursor;
    if (!before || isPlaceholderCursor(before)) {
      refetch();
      return;
    }
    const currentEdgeIds = new Set(
      (liveEdges ?? []).map((e) => e?.node?.clientId)
    );
    fetchMore({
      variables: {
        chatId: activeChatId,
        first: null, // override original first: firstPageCount — backend must not receive both first and last
        last: perPageCount,
        before,
      },
    })
      .then((result) => {
        const newEdges = result.data?.listMessages?.edges ?? [];
        const firstTrulyNew = newEdges.find(
          (e) => e?.node?.clientId && !currentEdgeIds.has(e.node.clientId)
        );
        if (firstTrulyNew?.node?.id) {
          setFetchedNewerMsgId(firstTrulyNew.node.id);
        }
      })
      .catch(() => {
        // A failed reverse fetch must not strand the open thread — fall back
        // to refetching page 1 so the new message still lands (TA-10150).
        refetch();
      });
  }, [livePageInfo, activeChatId, fetchMore, refetch, perPageCount, liveEdges]);
  fetchNewerMessagesRef.current = fetchNewerMessages;

  useEffect(() => {
    if (activeChatId && appStateVisible == 'active') {
      // Note: This refetch solves the problem of messages missing when coming in via notifications
      //   refetch();
    }
  }, [activeChatId, appStateVisible]);

  useEffect(() => {
    const subscription = AppState.addEventListener('change', (nextAppState) => {
      if (activeChatId && nextAppState == 'active') {
        debouncedRestartAll();
      }
      if (
        appState.current.match(/inactive|background/) &&
        nextAppState === 'active'
      )
        appState.current = nextAppState;
      setAppStateVisible(appState.current);
    });

    return () => {
      subscription.remove();
    };
  }, [activeChatId, restart, restartMessageDeleteSubscription]);

  // Web-specific focus detection (TA-8970)
  // AppState doesn't work reliably on web, so we use window focus events instead.
  // Read activeChatIdRef on demand instead of closing over activeChatId — this lets
  // us register the listener once (empty deps) and avoids the stale-closure window
  // between re-render and effect cleanup where the old handler could fire (TA-10061).
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    const handleFocus = () => {
      if (!activeChatIdRef.current) return;
      Sentry.addBreadcrumb({
        category: 'chat.focus',
        message: 'window.focus fired — triggering debouncedRestartAll',
        level: 'info',
        data: { activeChatId: activeChatIdRef.current },
      });
      debouncedRestartAll();
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []); // stable: reads ref on demand, no closure over activeChatId

  useEffect(() => {
    if (!activeChatId) return;
    const id = setInterval(() => {
      // Mirrors debouncedRestartAll — read the refs so a stale closure can't
      // fire on a skipped subscription; see shouldSkipRestart.
      if (
        shouldSkipRestart(
          activeChatIdRef.current,
          subscriptionSkippedRef.current
        )
      )
        return;
      restart();
      restartMessageDeleteSubscription();
      // refetch() removed: it re-fetches page 1 from the server on every tick,
      // resetting pageInfo and potentially triggering scroll jumps for users reading
      // older messages. Subscription heartbeat alone is sufficient to stay in sync.
      // TA-10153: reaction-lite backfill — recovers reaction updates whose
      // subscription events were missed, without touching pageInfo.
      refreshReactionsRef.current();
    }, SUBSCRIPTION_HEARTBEAT_MS);
    return () => clearInterval(id);
  }, [activeChatId, restart, restartMessageDeleteSubscription]);

  if (!activeChatId) return null;

  if (Platform.OS === 'web')
    return (
      <ChatDetail
        isPaginationResolved={isInitialMessagesResolved}
        hasNextPage={pageInfo?.hasNextPage ?? true}
        messages={allMessages}
        chatId={activeChatId}
        fetchAfterCursor={fetchAfterCursor}
        isInLiveList={isInLiveList}
        fetchLiveOlderPage={fetchLiveOlderPage}
        liveLoading={loading}
        refreshing={refreshing}
        loading={isSearchPreview ? nearbyLoading : loading}
        onRefresh={onRefresh}
        messageIdNeedScrollTo={xMessageId}
        noNeedRefetchRef={noNeedRefetchRef}
        fetchedNewerMsgId={fetchedNewerMsgId}
        onFetchedNewerMsgIdConsumed={() => setFetchedNewerMsgId(null)}
      />
    );

  return (
    <Sentry.TimeToInitialDisplay record={true}>
      <MyChatDetail
        hasNextPage={pageInfo?.hasNextPage ?? true}
        messages={allMessages}
        // TA-10885: offline, listMessages resolves from neither cache nor
        // network for a chat whose thread was never cached, and the thread
        // rendered blank. Hand the failure down so Chat Detail can say so.
        loadError={!!listMessagesError || !!nearbyMessagesError}
        showKeyboardFocus={showKeyboardFocus}
        chatId={activeChatId}
        messageId={messageId}
        fetchAfterCursor={fetchAfterCursor}
        refreshing={refreshing}
        loading={loading}
        onRefresh={onRefresh}
        messageIdNeedScrollTo={xMessageId}
      />
    </Sentry.TimeToInitialDisplay>
  );
};

export default Sentry.withProfiler(memo(ChatMessageData));
